import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { addMonths } from 'date-fns';
import { useCallback } from 'react';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import { GamesNavigator as GameplayGamesNavigator } from '@sorare/gameplay/src/components/seo/GamesNavigator';

import { useGamesNavigatorQuery } from '#src/hooks/useGamesNavigatorQuery.ts';

import type {
  ClubGamesNavigatorQuery,
  ClubGamesNavigatorQueryVariables,
} from './__generated__/index.graphql.ts';

const CLUB_GAMES_NAVIGATOR_QUERY = gql`
  query ClubGamesNavigatorQuery(
    $slug: String!
    $startDate: ISO8601DateTime!
    $endDate: ISO8601DateTime!
    $firstPast: Int
    $lastPast: Int
    $afterPast: String
    $beforePast: String
    $firstFuture: Int
    $lastFuture: Int
    $afterFuture: String
    $beforeFuture: String
  ) {
    football {
      club(slug: $slug) {
        slug
        pastGames: latestGames(
          first: $firstPast
          last: $lastPast
          after: $afterPast
          before: $beforePast
          includingLive: true
        ) {
          edges {
            cursor
          }
          nodes {
            id
            ...GamesNavigator_anyGameInterface
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
          }
        }
        futureGames: games(
          startDate: $startDate
          endDate: $endDate
          first: $firstFuture
          last: $lastFuture
          after: $afterFuture
          before: $beforeFuture
        ) {
          edges {
            cursor
          }
          nodes {
            id
            ...GamesNavigator_anyGameInterface
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
          }
        }
      }
    }
  }
  ${GameplayGamesNavigator.fragments.anyGameInterface}
` as TypedDocumentNode<
  ClubGamesNavigatorQuery,
  ClubGamesNavigatorQueryVariables
>;

type Props = { displayName: string; clubSlug: string };

const PAGE_SIZE = 5;
export const GamesNavigator = ({ displayName, clubSlug }: Props) => {
  const openDialog = useOpenDialog();
  const { data, loading, page, loadMore } = useGamesNavigatorQuery(
    CLUB_GAMES_NAVIGATOR_QUERY,
    {
      slug: clubSlug,
      startDate: new Date(),
      endDate: addMonths(new Date(), 2),
    },
    PAGE_SIZE
  );

  const loadPage = useCallback(
    (direction: 'previous' | 'next') => {
      loadMore(true, direction, {
        futureFirstCursor: data?.football.club.futureGames.edges[0]?.cursor,
        futureEndCursor: data?.football.club.futureGames.pageInfo.endCursor,
        pastFirstCursor: data?.football.club.pastGames.edges[0]?.cursor,
        pastEndCursor: data?.football.club.pastGames.pageInfo.endCursor,
      });
    },
    [data, loadMore]
  );

  const pastGames = page <= 0 ? data?.football.club.pastGames.nodes || [] : [];
  const futureGames =
    page >= 0 ? data?.football.club.futureGames.nodes || [] : [];

  return (
    <GameplayGamesNavigator
      displayName={displayName}
      hasPreviousPage={
        !!data?.football.club.pastGames.pageInfo.hasNextPage ||
        !!data?.football.club.futureGames.pageInfo.hasPreviousPage
      }
      hasNextPage={
        !!data?.football.club.futureGames.pageInfo.hasNextPage ||
        !!data?.football.club.pastGames.pageInfo.hasPreviousPage
      }
      loadPage={loadPage}
      loading={loading}
      futureGames={futureGames}
      pastGames={pastGames}
      onClick={id => openDialog(DialogKey.game, id)}
      pageSize={PAGE_SIZE}
    />
  );
};
